<template>
  <b-card>
    <p>Copy and paste from spreadsheet. <strong>Max 100 rows</strong> at a time.</p>
    <template v-slot:header>
      <span class="breadcrumbs"><b-link :to="{name: `project`}">Videos</b-link> / Batch Create</span>
    </template>

    <div class="input-table">
      <hot-table :settings="table_settings" ref="data_table"></hot-table>
    </div>

    <p class='text-muted text-center'><small>As you add data more rows will appear</small></p>

    <LoadingButton :loading="loading" @click="create_batch">Add</LoadingButton>

  </b-card>
</template>

<script>
import { required, minLength } from "vuelidate/lib/validators"
import ItemMixin from "@/mixins/Item.js"
import ConstantsMixin from "@/mixins/Constants.js"
import { db, FieldValue, config } from '@/main.js'
import moment from 'moment/src/moment'

import api from '@/lib/api.js'

import { HotTable } from '@handsontable/vue'

export default {
  metaInfo: {title: 'Item batch create'},
  mixins: [ItemMixin, ConstantsMixin],
  props: ['project', 'profile'],
  components: {HotTable},
  validations: {
    item: {
      name: {required, minLength: minLength(3)},
    }
  },
  mounted() {
    if (this.$refs.data_table && this.$refs.data_table.hotInstance) {
      this.$refs.data_table.hotInstance.render()
    }
  },
  beforeRouteLeave (to, from, next) {
    if (to.name === 'project' || to.name === 'item') {
      next()
      return
    }

    const answer = window.confirm('Do you really want to leave? you have unsaved changes!')
    if (answer) {
      next()
    } else {
      next(false)
    }
  },
  data() {
    let variables = []
    let headers = []
    let columns = []
    for (let variable of this.project.variables) {
      // variables.push({word: variable, replace_with: null})
      headers.push(variable['word'].replace(/^\w/, (c) => c.toUpperCase())) // Capitalise
      columns.push({})
    }

    if (this.project.presentation === 'website') {
      headers.push('Website URL')
      columns.push({})
    }

    headers.push('Email')
    columns.push({})

    return {
      table_settings: {
        data: [],
        columns: columns,
        colHeaders: headers,
        // colHeaders: true,
        rowHeaders: true,
        minSpareRows: 10,
        maxRows: 100,
        width: '100%',
        stretchH: 'all',
        height: '100%',
        licenseKey: 'non-commercial-and-evaluation',
      },
      param: 'item',
      collection: 'item',
      data: [],
      variables: variables,
      loading: false,
    }
  },
  methods: {
    async create_batch() {
      this.loading = true
      let rows = this.$refs.data_table.hotInstance.getData()

      let items = []
      for (let row_i in rows) {
        let row = rows[row_i]
        let all_null = true
        for (let col of row) {
          if (col !== null && col.trim() !== '') {
            all_null = false
          }
        }

        if (all_null) {
          continue
        }

        let item = {
          updated: new Date(),
          created: new Date(),
          user: db.collection('user').doc(this.profile.id),
          team: db.collection('team').doc(this.profile.team.id),
          status: 'pending',
          video_url: null,
          error_message: null,
          video_player_url: null,
          rerendered: false,
          overlay_text: null,
          variables: [],
          played: 0,
          visited: 0,
          finished: 0,
          clicked: 0,
        }

        let i = 0
        let first_name = null
        for (let variable of this.project.variables) {
          let replace_with = row[i]
          item.variables.push({word: variable['word'], replace_with: replace_with})

          if (variable.word == 'name') {
            item.overlay_text = `Hey ${replace_with}`
            first_name = replace_with
          }

          if (!replace_with) {
            this.$swal({title: 'Incomple replacement', text: "Please make sure every variable has a replacement", icon: 'warning'})
            this.loading = false
            return
          }

          i++
        }

        item.name = row[0] || first_name || `${moment().format('HH:MM DD/MM/YY')} - ${parseInt(row_i)+1}`,
        item.website = null
        item.email = null

        if (this.project.presentation === 'website' && row[row.length-2] && row[row.length-2].trim() != '') {
          let website_url = row[row.length-2]

          if (website_url.split('@').length === 2) { // if email
            item.email = website_url.toLowerCase().trim()
          } else if (!website_url.match(/https?:\/\//i)) {
            website_url = `http://${website_url}`
          }

          item.website = website_url
        }

        if (row[row.length-1] && row[row.length-1].trim() != '' && row[row.length-1].split('@').length === 2) {
          item.email = row[row.length-1].toLowerCase().trim()
        }

        items.push(item)
      }

      if (items.length === 0) {
        this.loading = false
        this.$swal({title: 'Add item', text: "Please add at least one item", icon: 'warning'})
        return
      }

      let proms = []
      for (let item of items) {
        let item_id = db.collection('item').doc().id
        item.video_player_url = `https://${config.firebase_config.video_domain}/video/?project=${encodeURIComponent(this.project.id)}&item=${encodeURIComponent(item_id)}`
        proms.push(db.collection('project').doc(this.$route.params.id).collection('item').doc(item_id).set(item))
        item.id = item_id
      }

      await Promise.all(proms)

      let update = {updated: new Date()}
      update[`usage.${this.profile.id}.recipients`] = FieldValue.increment(items.length)
      await db.collection('team').doc(this.profile.team.id).collection('period').doc(this.profile.team.current_period.id).update(update)

      await db.collection('project').doc(this.$route.params.id).update({updated: new Date(), items_count: FieldValue.increment(items.length)})

      if (items.length === 1) {
        this.loading = false
        this.$router.push({name: 'item', params: {item: items[0].id, from_item: items[0]}})
      } else {
        await this.$swal({title: 'Items added', text: `${items.length} items have been added to your project`, icon: 'success'})
        this.loading = false
        this.$router.push({name: 'project'})
      }
    }
  }
}
</script>

<style src="../../../../node_modules/handsontable/dist/handsontable.full.css"></style>
<style>
.input-table {
    height: 280px;
    margin-bottom: 0px;
}
</style>
